import { ModalShell } from '@busy/ui';
import { useT } from '../lib/i18n';

// Celebration modal that fires whenever a barber earns a new cooloff
// or shield. Three visual variants:
//
//   'cooloff'         — single cooloff earned. Snowfall effect.
//   'shield-earned'   — single shield earned (every 100 alive days).
//                        Lightning effect.
//   'shield-grouped'  — 3 shields granted at once (starter or
//                        anniversary). Balloons rising. Shows ×count
//                        in the header, three icon stack in the badge.
//
// Mounted at app root behind a queue (App.tsx). Each item in the queue
// renders one modal; on dismiss, the next item shows. Retro state on
// first ever app open caps to one of each kind so a long-streaked
// user doesn't get spammed with 10 popups in a row — see App.tsx.
//
// Architecture intentionally simpler than the achievement
// CelebrationModal: no artwork, no nickname computation, no share
// flow. Just the earn moment.

export type StreakCelebrationKind = 'cooloff' | 'shield-earned' | 'shield-grouped';

export function StreakProtectionCelebrationModal({
  kind,
  count,
  onDismiss,
}: {
  kind: StreakCelebrationKind;
  count: number;
  onDismiss: () => void;
}) {
  const t = useT();
  const icon =
    kind === 'cooloff' ? <SnowflakeBig /> : <ShieldBig />;
  const titleKey =
    kind === 'cooloff'
      ? 'streakCelebrate.cooloff.title'
      : kind === 'shield-earned'
      ? 'streakCelebrate.shieldEarned.title'
      : 'streakCelebrate.shieldGrouped.title';
  const bodyKey =
    kind === 'cooloff'
      ? 'streakCelebrate.cooloff.body'
      : kind === 'shield-earned'
      ? 'streakCelebrate.shieldEarned.body'
      : 'streakCelebrate.shieldGrouped.body';
  const eyebrowKey =
    kind === 'cooloff' ? 'streakCelebrate.cooloff.eyebrow' : 'streakCelebrate.shield.eyebrow';

  return (
    <ModalShell
      open
      onClose={onDismiss}
      variant="card"
      size="md"
      ariaLabel={t(titleKey, { count })}
    >
      <div className="bg-surface rounded-2xl relative overflow-hidden">
        {/* Effect overlay layer — covers the modal but pointer-events
            none so the dismiss button remains tappable. */}
        {kind === 'cooloff' && <SnowfallOverlay />}
        {kind === 'shield-earned' && <LightningOverlay />}
        {kind === 'shield-grouped' && <BalloonsOverlay />}

        <div className="relative bg-navy-900 text-white px-6 pt-6 pb-5 rounded-t-2xl">
          <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-gold-400 mb-1">
            {t(eyebrowKey)}
          </div>
          <h2 className="font-display text-2xl leading-tight">
            {t(titleKey, { count })}
          </h2>
        </div>

        <div className="relative p-6 flex flex-col items-center gap-3">
          {/* Badge — for grouped shields, show three icons offset to
              hint at the count. For singles, just one. */}
          {kind === 'shield-grouped' && count >= 2 ? (
            <div className="relative w-32 h-32 grid place-items-center">
              <div className="absolute -left-4 top-2 opacity-60 scale-90">{icon}</div>
              <div className="absolute -right-4 top-2 opacity-60 scale-90">{icon}</div>
              <div className="relative">{icon}</div>
              <div className="absolute -bottom-1 right-0 bg-gold-500 text-navy-900 text-[11px] font-extrabold px-2 py-0.5 rounded-full">
                ×{count}
              </div>
            </div>
          ) : (
            <div className="w-28 h-28 grid place-items-center">{icon}</div>
          )}
          <p className="text-center text-[14px] text-ink-700 leading-relaxed max-w-sm">
            {t(bodyKey, { count })}
          </p>
          <button
            type="button"
            onClick={onDismiss}
            className="mt-2 w-full h-12 rounded-xl bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold text-[14px]"
          >
            {t('streakCelebrate.gotIt')}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ─── badge icons (large, gold) ───────────────────────────────────────
function SnowflakeBig() {
  return (
    <svg className="w-24 h-24 text-gold-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="12" y1="2" x2="12" y2="22" />
      <path d="m20 16-4-4 4-4" />
      <path d="m4 8 4 4-4 4" />
      <path d="m16 4-4 4-4-4" />
      <path d="m8 20 4-4 4 4" />
    </svg>
  );
}

function ShieldBig() {
  return (
    <svg className="w-24 h-24 text-gold-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

// ─── effect overlays (lightweight CSS keyframes, no libs) ────────────

// Snowfall — white circles drifting downward at staggered delays so it
// looks continuous. Mirrors the demo's flame/confetti pattern.
function SnowfallOverlay() {
  const flakes = [
    { l: '8%', size: 6, delay: 0,   dur: 4.2 },
    { l: '18%', size: 4, delay: 1.2, dur: 5.0 },
    { l: '28%', size: 7, delay: 0.4, dur: 3.8 },
    { l: '40%', size: 5, delay: 2.0, dur: 4.6 },
    { l: '52%', size: 6, delay: 0.8, dur: 4.4 },
    { l: '64%', size: 4, delay: 1.6, dur: 5.2 },
    { l: '76%', size: 6, delay: 0.2, dur: 4.0 },
    { l: '88%', size: 5, delay: 1.0, dur: 4.8 },
  ];
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none z-10">
      {flakes.map((f, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-white/85 shadow-[0_0_8px_rgba(255,255,255,0.6)]"
          style={{
            left: f.l,
            top: '-10%',
            width: f.size,
            height: f.size,
            animation: `snowfall ${f.dur}s linear ${f.delay}s infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes snowfall {
          0%   { transform: translateY(0) translateX(0); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateY(120vh) translateX(20px); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// Lightning — cyan-white flashes fanning across the modal. Quick
// staccato pulses give the "earned 1 shield" moment some snap.
function LightningOverlay() {
  const bolts = [
    { l: '15%', t: '20%', delay: 0,   rot: -20 },
    { l: '70%', t: '15%', delay: 0.6, rot: 15  },
    { l: '40%', t: '60%', delay: 0.3, rot: -8  },
    { l: '80%', t: '70%', delay: 0.9, rot: 25  },
  ];
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none z-10 overflow-hidden">
      {bolts.map((b, i) => (
        <svg
          key={i}
          viewBox="0 0 24 24"
          className="absolute w-12 h-12 text-cyan-200 drop-shadow-[0_0_12px_rgba(125,211,252,0.9)]"
          style={{
            left: b.l,
            top: b.t,
            transform: `rotate(${b.rot}deg)`,
            animation: `lightning 1.6s ease-out ${b.delay}s infinite`,
          }}
        >
          <path d="M13 2L3 14h7l-2 8 10-12h-7l2-8z" fill="currentColor" />
        </svg>
      ))}
      <style>{`
        @keyframes lightning {
          0%, 100% { opacity: 0; transform: rotate(var(--rot, 0deg)) scale(0.85); }
          10%      { opacity: 1; transform: rotate(var(--rot, 0deg)) scale(1.05); }
          20%      { opacity: 0.4; }
          30%      { opacity: 1; }
          40%      { opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// Balloons — colored circles rising from the bottom of the modal.
// Mirrors the iOS Messages "happy birthday" effect.
function BalloonsOverlay() {
  const balloons = [
    { l: '8%',  color: '#ef4444', delay: 0,   dur: 5.0, size: 24 },
    { l: '22%', color: '#f59e0b', delay: 0.6, dur: 5.6, size: 20 },
    { l: '38%', color: '#10b981', delay: 0.3, dur: 5.2, size: 26 },
    { l: '52%', color: '#3b82f6', delay: 0.9, dur: 4.8, size: 22 },
    { l: '66%', color: '#8b5cf6', delay: 0.4, dur: 5.4, size: 24 },
    { l: '80%', color: '#ec4899', delay: 0.7, dur: 5.0, size: 20 },
  ];
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none z-10 overflow-hidden">
      {balloons.map((b, i) => (
        <span
          key={i}
          className="absolute"
          style={{
            left: b.l,
            bottom: '-15%',
            animation: `balloons ${b.dur}s ease-in ${b.delay}s infinite`,
          }}
        >
          {/* The balloon body — a gradient-shaded circle with a tiny
              shine highlight. The string is drawn as an SVG line below
              the balloon. */}
          <span
            className="block rounded-full"
            style={{
              width: b.size,
              height: b.size + 4,
              background: `radial-gradient(circle at 35% 30%, rgba(255,255,255,.55), ${b.color} 65%)`,
              boxShadow: `0 0 16px ${b.color}55`,
            }}
          />
          <span
            className="block mx-auto bg-ink-500/40"
            style={{ width: 1, height: 18 }}
          />
        </span>
      ))}
      <style>{`
        @keyframes balloons {
          0%   { transform: translateY(0) translateX(0); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateY(-130vh) translateX(15px); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
